// ASHFALL: DOMINION — app bootstrap and scene controller. Faction-select → battle →
// result → menu, plus the campaign layer (province map → launches battles → applies
// results). Everything renders to one full-window canvas.

import type { Faction } from "./sim/types";
import { BattleScene, BattleResult } from "./game/game";
import { MenuScene, ResultScene, StartOpts } from "./game/menus";
import { CampaignScene, CampaignState, applyPlayerBattle, createCampaign, loadCampaign, saveCampaign } from "./game/campaign";
import { ScreenBase } from "./game/screen";

class App {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private screen: ScreenBase | null = null;
  private battle: BattleScene | null = null;
  private lastFaction: Faction = "legion";
  private lastOpts: StartOpts = {};
  private campaign: CampaignState | null = null;

  constructor() {
    const app = document.getElementById("app")!;
    this.canvas = document.createElement("canvas");
    // The 2D canvas is the top layer: it draws the menus/result/campaign chrome and, in
    // battle, the HUD + world overlay. In 3D battle mode a WebGL canvas is inserted behind
    // it (z-index 1) by PcRenderer, so this canvas must be transparent where nothing is
    // drawn — hence alpha:true and an explicit stacking order.
    this.canvas.style.cssText = "position:absolute;inset:0;z-index:2;";
    app.appendChild(this.canvas);
    const ctx = this.canvas.getContext("2d", { alpha: true });
    if (!ctx) throw new Error("2D canvas context unavailable");
    this.ctx = ctx;
    window.addEventListener("resize", () => this.battle?.resize());
    this.showMenu();
  }

  private clear(): void {
    if (this.screen) {
      this.screen.unmount();
      this.screen = null;
    }
    if (this.battle) {
      this.battle.stop();
      this.battle = null;
    }
  }

  showMenu(): void {
    this.clear();
    this.screen = new MenuScene(this.canvas, this.ctx, (f, o) => this.startBattle(f, o), (f) => this.showCampaign(f));
    this.screen.mount();
  }

  startBattle(faction: Faction, opts: StartOpts): void {
    this.clear();
    this.lastFaction = faction;
    this.lastOpts = opts;
    this.battle = new BattleScene(this.canvas, this.ctx, faction, opts, (r) => this.onBattleEnd(r));
    this.battle.start();
  }

  private onBattleEnd(result: BattleResult): void {
    if (this.lastOpts.spectate) {
      this.showMenu();
      return;
    }
    this.clear();
    this.screen = new ResultScene(
      this.canvas,
      this.ctx,
      result,
      this.lastFaction,
      () => this.startBattle(this.lastFaction, {}),
      () => this.showMenu(),
    );
    this.screen.mount();
  }

  // ---- campaign ----
  showCampaign(faction?: Faction): void {
    this.clear();
    if (!this.campaign) this.campaign = loadCampaign() ?? createCampaign(faction ?? "legion");
    const state = this.campaign;
    this.screen = new CampaignScene(this.canvas, this.ctx, state, {
      onAttackBattle: (provinceId) => this.startCampaignBattle(provinceId),
      onBack: () => this.showMenu(),
    });
    this.screen.mount();
  }

  private startCampaignBattle(provinceId: number): void {
    const camp = this.campaign!;
    this.clear();
    this.lastFaction = camp.playerFaction;
    this.lastOpts = {};
    this.battle = new BattleScene(this.canvas, this.ctx, camp.playerFaction, {}, (r) => {
      applyPlayerBattle(camp, provinceId, r.phase === "won");
      saveCampaign(camp);
      this.showCampaign();
    });
    this.battle.start();
  }
}

new App();
